import { getFromServerPublicTopic, getFromServerTopic } from "@utility/slice";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { logFilter } from "@logging";
import { talkToServer } from "@helpers/servercomms";
import { IDLE, LOADING } from "@src/constants";
import { formatDate, formatTime } from "@helpers/utils";
import { MILLIS_PER_MINUTE } from "@helpers/time.js";

export const initializeLoginPage = getFromServerPublicTopic({
  topic: "auth",
  action: "getSsoDomains",
});

export const ssDataTableRefresh = createAsyncThunk(
  "ssDataTableRefresh",
  async (options, api) => {
    const request = {
      context: {
        topic: "serversidetable",
        action: "refresh",
        options,
        useCache: false,
      },
    };
    const state = api.getState();
    if (false) logFilter(tag, "here is state", state, options);
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const saveFloorInfo = createAsyncThunk(
  "saveFloorInfo",
  async (options, api) => {
    const request = {
      context: {
        topic: "buildings",
        action: "savefloorlist",
        options: options.options,
        useCache: false,
      },
      payload: options.payload,
    };
    const state = api.getState();
    if (false) logFilter(tag, "here is state", state, options);
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

const performSaveManageInfo = getFromServerTopic({
  topic: "buildings",
  action: "saveManageInfo",
  contextOptionsFn: async ({ arg, thunkAPI: { getState } }) => {
    const { siteId, squareFeet, weekdayHours, saturdayHours, sundayHours } =
      arg;
    const manageInfo = getState().appData.manageInfo;

    let weekdayStartTime = undefined;
    let weekdayEndTime = undefined;
    let saturdayStartTime = undefined;
    let saturdayEndTime = undefined;
    let sundayStartTime = undefined;
    let sundayEndTime = undefined;

    if (weekdayHours?.isEnabled) {
      weekdayStartTime = formatTime(weekdayHours.hours[0]);
      weekdayEndTime = formatTime(weekdayHours.hours[1]);
    }
    if (saturdayHours?.isEnabled) {
      saturdayStartTime = formatTime(saturdayHours.hours[0]);
      saturdayEndTime = formatTime(saturdayHours.hours[1]);
    }
    if (sundayHours?.isEnabled) {
      sundayStartTime = formatTime(sundayHours.hours[0]);
      sundayEndTime = formatTime(sundayHours.hours[1]);
    }

    return squareFeet
      ? {
          ...arg,
          weekdayStartTime,
          weekdayEndTime,
          saturdayStartTime,
          saturdayEndTime,
          sundayStartTime,
          sundayEndTime,
        }
      : {
          siteId,
          comfortZoneLowTempF: manageInfo.comfortZoneLowTempF,
          comfortZoneHighTempF: manageInfo.comfortZoneHighTempF,
          comfortZoneStartTime: formatTime(manageInfo.comfortZoneStartTime),
          comfortZoneStopTime: formatTime(manageInfo.comfortZoneStopTime),
          isRecalculateHistory: manageInfo.isRecalculateHistory,
        };
  },
});

export const getNotes = getFromServerTopic({
  topic: "buildings",
  action: "getNotes",
  contextOptionsFn: ({ arg }) => arg,
  payloadFn: async () => {},
});

export const getNoteDetails = getFromServerTopic({
  topic: "buildings",
  action: "getNoteDetails",
  contextOptionsFn: ({ arg: { noteId } }) => ({ noteId }),
  payloadFn: async (payload) => payload,
});

export const getEntitySharingContacts = getFromServerTopic({
  topic: "buildings",
  action: "getEntitySharingContacts",
  contextOptionsFn: ({ arg: { datapointId, siteId } }) => ({
    datapointId,
    siteId,
  }),
  payloadFn: async () => {},
});

export const performDeleteNote = getFromServerTopic({
  topic: "buildings",
  action: "deleteNote",
  contextOptionsFn: ({ payload: { id, siteId } }) => ({ id, siteId }),
  payloadFn: (payload) => payload,
});

export const deleteNote = createAsyncThunk(
  "appdata.deleteNote",
  async (payload, { dispatch }) => {
    await dispatch(performDeleteNote(payload));
    const { isPortfolio, siteId } = payload;
    return dispatch(
      getNotes({
        isPortfolio,
        siteId: isPortfolio ? null : siteId,
      }),
    );
  },
);

export const performSaveNote = getFromServerTopic({
  topic: "buildings",
  action: "saveNote",
  contextOptionsFn: ({
    payload: {
      id,
      component,
      context,
      isSkipUpdateEmail,
      shareWith,
      siteId,
      tags,
    },
  }) => ({
    id,
    component,
    context,
    isSkipUpdateEmail,
    shareWith,
    siteId,
    tags,
  }),
  payloadFn: (payload) => payload,
});

export const saveNote = createAsyncThunk(
  "appdata.saveNote",
  async (payload, { dispatch }) => {
    await dispatch(performSaveNote(payload));
    const { isPortfolio, siteId } = payload;
    return dispatch(
      getNotes({
        isPortfolio,
        siteId: isPortfolio ? null : siteId,
      }),
    );
  },
);

export const performSaveNoteComment = getFromServerTopic({
  topic: "buildings",
  action: "saveNoteComment",
  contextOptionsFn: ({ arg: { addedAt, noteId, comment, isClose } }) => ({
    addedAt,
    noteId,
    comment,
    isClose,
  }),
  payloadFn: () => {},
});

export const saveNoteComment = createAsyncThunk(
  "appdata.saveNoteComment",
  async (arg, { dispatch }) => {
    await dispatch(performSaveNoteComment(arg));
    return dispatch(
      getNoteDetails({
        noteId: arg.noteId,
      }),
    );
  },
);

export const getOperationsDataByDatapointIdAndTimestampBetween =
  getFromServerTopic({
    topic: "datapoints",
    action: "getOperationsDataByDatapointIdAndTimestampBetween",
    contextOptionsFn: async ({ payload }) => payload,
    payloadFn: async (payload) => payload,
  });

export const getStartupsBySiteIdAndTimestampBetween = getFromServerTopic({
  topic: "buildings",
  action: "getStartupsBySiteIdAndTimestampBetween",
  contextOptionsFn: async ({ payload }) => payload,
  payloadFn: async (payload) => payload,
});

export const getHourlyWeatherDataByStationAndTimestampRange =
  getFromServerTopic({
    topic: "weatherHourlyData",
    action: "getHourlyWeatherDataByStationAndTimestampRange",
    payloadFn: async (payload) => {
      return await payload;
    },
  });

export const getForecastWeatherDataByStationAndLocalTimestampRange =
  getFromServerTopic({
    topic: "weatherForecastData",
    action: "getForecastWeatherDataByStationAndLocalTimestampRange",
    contextOptionsFn: async ({ payload }) => payload,
    payloadFn: async (payload) => await payload,
  });

// Yet to be used for something
export const getDailyWeatherStatisticsBySiteAndDateRangeInclusive =
  getFromServerTopic({
    topic: "weatherDailyStats",
    action: "getDailyWeatherStatisticsBySiteAndDateRangeInclusive",
    contextOptionsFn: async ({ payload }) => payload,
    payloadFn: async (payload) => await payload,
  });

export const getMostRecentOrSimilarDayData = getFromServerTopic({
  topic: "weatherStation",
  action: "getMostRecentOrSimilarDayData",
  contextOptionsFn: async ({ payload }) => payload,
  payloadFn: async (payload) => await payload,
});

export const getWellstatWBRForDeviceManagement = createAsyncThunk(
  "equipment/getWellstatWBRForDeviceManagement",
  async (options) => {
    const request = {
      context: {
        topic: "equipment",
        action: "findwbrbyid",
        options: options.options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getWellstatWAQForDeviceManagement = createAsyncThunk(
  "equipment/getWellstatWAQForDeviceManagement",
  async (options) => {
    const request = {
      context: {
        topic: "equipment",
        // action: "findwaqbypersistentid",
        action: "findwaqbyid",
        options: options.options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getWellstatDevicesForSite = createAsyncThunk(
  "equipment/getWellstatDevicesForSite",
  async (options, api) => {
    const request = {
      context: {
        topic: "equipment",
        action: "getwellstatforsite",
        options: options.options,
        useCache: false,
      },
    };
    const state = api.getState();
    if (false) logFilter(tag, "here is state", state, options);
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getWellstatDataForDevices = createAsyncThunk(
  "equipment/getWellstatDataForDevices",
  async (options) => {
    const request = {
      context: {
        topic: "equipment",
        action: "getwellstatdatafordevices",
        options: options.options,
        useCache: false,
      },
    };
    const response = await talkToServer(request);
    if (response && response !== undefined && response.rc === "OK") {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getUtilityAccounts = createAsyncThunk(
  "appdata/getUtilityAccounts",
  async (options) => {
    const request = {
      context: {
        topic: "buildings",
        action: "listaccountsforbuilding",
        options: options.options,
        useCache: false,
      },
      payload: options.payload,
    };
    logFilter("table", "call to server ...!!", request);
    const response = await talkToServer(request);
    logFilter("table", "back with getUtilityAccounts data", response);
    if (response && response !== undefined) {
      logFilter("table", "back with getUtilityAccounts data good?", response);
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const saveFlyOnNote = createAsyncThunk(
  "appdata/saveFlyOnNote",
  async (options) => {
    const request = {
      context: {
        topic: "intervals",
        action: "saveflyonnote",
        options: options.options,
        useCache: false,
      },
      payload: options.payload,
    };
    const response = await talkToServer(request);
    if (response && response !== undefined) {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getBuildingTableData = createAsyncThunk(
  "appdata/getBuildingTableData",
  async (options) => {
    const request = {
      context: {
        topic: "buildings",
        action: "forTable",
        options,
        useCache: false,
      },
      payload: {},
    };
    const response = await talkToServer(request);
    if (response) {
      return {
        response,
      };
    }
  },
);

export const getIntervalsForDateRange = createAsyncThunk(
  "appdata/getIntervalsForDateRange",
  async ({ options }) => {
    const request = {
      context: {
        topic: "intervals",
        action: "getrangemultiple",
        options,
        useCache: false,
      },
      payload: {},
    };
    const response = await talkToServer(request);
    if (response && response !== undefined) {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getDataPointsForSite = createAsyncThunk(
  "appdata/getDataPointsForSite",
  async (options /*, api*/) => {
    const request = {
      context: {
        topic: "buildings",
        action: "listdatapointsforbuilding",
        options: options.options,
        useCache: false,
      },
      payload: {},
    };
    const response = await talkToServer(request);
    if (response) {
      return {
        response,
      };
    }
  },
);

const performSaveDatapointName = getFromServerTopic({
  topic: "datapoints",
  action: "saveDatapointName",
  contextOptionsFn: async ({ arg }) => arg,
});

export const saveDatapointName = createAsyncThunk(
  "appdata.saveDatapointName",
  async (arg, { dispatch }) => {
    await dispatch(performSaveDatapointName(arg));
    return dispatch(
      getDataPointsForSite({
        options: { site_id: arg.siteId, type: "EIMS" },
      }),
    );
  },
);

export const getAllAccountsForSite = createAsyncThunk(
  "appdata/getAccountsByActiveSite",
  async (options /*, api*/) => {
    const request = {
      context: {
        topic: "accounts",
        action: "getAccountsByActiveSite",
        options: options.options,
        useCache: false,
      },
      payload: {
        siteId: options.options.site_id,
      },
    };
    const tag = "appData::getAccountsByActiveSite";
    const response = await talkToServer(request);
    if (response) {
      logFilter(tag, "back with account data good?", response);
      return {
        response,
      };
    }
  },
);

export const getActiveAccountsForSite = createAsyncThunk(
  "appdata/getActiveAccountsBySite",
  async (options /*, api*/) => {
    const request = {
      context: {
        topic: "accounts",
        action: "getActiveAccountsBySite",
        options: options.options,
        useCache: false,
      },
      payload: {
        siteId: options.options.site_id,
      },
    };
    const tag = "appData::getActiveAccountsBySite";
    const response = await talkToServer(request);
    if (response) {
      logFilter(tag, "back with account data good?", response);
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

export const getBuildingInfo = createAsyncThunk(
  "appdata/getBuildingInfo",
  async (options, api) => {
    const tag = "appData::getBuildingInfo";
    const request = {
      context: {
        topic: "buildings",
        action: "getBuildingInfo",
        options: options.options,
        useCache: false,
      },
      payload: {},
    };
    // see if we have this data locally already in the store
    let doFetch = true;
    const state = api.getState();
    if (
      state.appData.buildingData &&
      state.appData.buildingData[options.options.site_id] &&
      state.appData.buildingData[options.options.site_id].info
    ) {
      doFetch = false;
    }
    logFilter(tag, doFetch, options.options);
    if (doFetch || options.options.refresh) {
      const response = await talkToServer(request);
      logFilter(tag, "back with building data", response);
      if (response && response !== undefined) {
        return {
          response, // pass along what we get back from the server
        };
      }
    }
  },
);

export const saveManageInfo = createAsyncThunk(
  "appdata.saveManageInfo",
  async (payload, { dispatch }) => {
    await dispatch(performSaveManageInfo(payload));
    return dispatch(
      getBuildingInfo({
        options: { site_id: payload, refresh: true },
      }),
    );
  },
);

export const setBuildingTotalizer = createAsyncThunk(
  "buildings/setBuildingTotalizer",
  async (arg, { dispatch }) => {
    await dispatch(
      getFromServerTopic({
        topic: "buildings",
        action: "setBuildingTotalizer",
        contextOptionsFn: async ({ payload }) => payload,
        payloadFn: async (payload) => payload,
      })(arg),
    );

    return await dispatch(
      getDataPointsForSite({
        options: { site_id: arg.siteId, type: "EIMS" },
      }),
    );
  },
);

export const setReportingEnabledForAccount = createAsyncThunk(
  "buildings/setReportingEnabledForAccount",
  async (arg, { dispatch }) => {
    await dispatch(
      getFromServerTopic({
        topic: "buildingReporting",
        action: "setReportingEnabledForAccount",
        contextOptionsFn: ({ arg }) => arg,
        payloadFn: (payload) => payload,
      })(arg),
    );

    return dispatch(
      getDataPointsForSite({
        options: { site_id: arg.siteId, type: "EIMS" },
      }),
    );
  },
);

export const updateEquipmentFields = getFromServerTopic({
  topic: "equipment",
  action: "updateEquipmentFields",
  contextOptionsFn: async ({ arg }) => arg,
  payloadFn: async (arg) => arg,
});

export const getBuildingLayout = createAsyncThunk(
  "appdata/getBuildingLayout",
  async (options, api) => {
    const tag = "appData::getBuildingLayout";
    const request = {
      context: {
        topic: "buildings",
        action: "getBuildingLayout",
        options: options.options,
        useCache: false,
      },
      payload: {},
    };
    // see if we have this data locally already in the store
    let doFetch = true;
    const state = api.getState();
    if (
      state.appData.buildingData &&
      state.appData.buildingData[options.options.site_id] &&
      state.appData.buildingData[options.options.site_id].layout
    ) {
      doFetch = false;
    }
    logFilter(tag, doFetch, options.options);
    if (doFetch || options.options.refresh) {
      const response = await talkToServer(request);
      if (response && response !== undefined) {
        return {
          response, // pass along what we get back from the server
        };
      }
    }
  },
);

export const getAllDatapointsForSite = createAsyncThunk(
  "appdata/getAllDatapointsForSite",
  async ({ siteId }, { dispatch }) => {
    return Promise.all([
      dispatch(
        getDataPointsForSite({
          options: { site_id: siteId, type: "BMS" },
        }),
      ),
      dispatch(
        getDataPointsForSite({
          options: { site_id: siteId, type: "EIMS" },
        }),
      ),
      dispatch(
        getDataPointsForSite({
          options: { site_id: siteId, type: "IAQ_AVERAGES" },
        }),
      ),
      dispatch(
        getDataPointsForSite({
          options: { site_id: siteId, type: "IAQMS" },
        }),
      ),
      dispatch(
        getDataPointsForSite({
          options: { site_id: siteId, type: "OCCUPANCY" },
        }),
      ),
      dispatch(
        getDataPointsForSite({
          options: { site_id: siteId, type: "TENANT_SUBMETERS" },
        }),
      ),
    ]);
  },
);

export const setGaugeEnabledForDatapoint = createAsyncThunk(
  "buildings/setGaugeEnabledForDatapoint",
  async (arg, { dispatch }) => {
    await dispatch(
      getFromServerTopic({
        topic: "buildingReporting",
        action: "setGaugeEnabledForDatapoint",
        contextOptionsFn: ({ arg }) => arg,
      })(arg),
    );

    return dispatch(
      getAllDatapointsForSite({
        siteId: arg.siteId,
      }),
    );
  },
);

export const initializeBuilding = createAsyncThunk(
  "appdata/initializeBuilding",
  async ({ siteId }, { dispatch }) => {
    return Promise.all([
      dispatch(
        getBuildingInfo({
          options: { site_id: siteId, refresh: true },
        }),
      ),
      dispatch(
        getWellstatDevicesForSite({
          options: { site_id: siteId, refresh: true },
        }),
      ),
      dispatch(getAllDatapointsForSite({ siteId })),
      dispatch(getActiveAccountsForSite({ options: { site_id: siteId } })),
      dispatch(getAllAccountsForSite({ options: { site_id: siteId } })),
    ]);
  },
);

export const getEnergyStarRatings = createAsyncThunk(
  "appdata/getEnergyStarRatings",
  async (options, api) => {
    const tag = "appData::getEnergyStarRatings";
    const request = {
      context: {
        topic: "buildings",
        action: "getEnergyStarRatings",
        options: options.options,
        useCache: false,
      },
      payload: {},
    };
    // see if we have this data locally already in the store
    let doFetch = true;
    const state = api.getState();
    if (
      state.appData.buildingData &&
      state.appData.buildingData[options.options.site_id] &&
      state.appData.buildingData[options.options.site_id].energyStarRatings
    ) {
      doFetch = false;
    }
    logFilter(
      tag,
      "here is doFetch",
      doFetch,
      " and options ",
      options.options,
    );
    if (doFetch || options.options.refresh) {
      const response = await talkToServer(request);
      if (response && response !== undefined) {
        return {
          response, // pass along what we get back from the server
        };
      }
    }
  },
);

export const getDatapointIntervalsForChart = createAsyncThunk(
  "appdata/getDatapointIntervalsForChart",
  async (options, api) => {
    const request = {
      context: {
        topic: "intervals",
        action: "getrangeforchartsingle",
        options: options.options,
        useCache: false,
      },
      payload: {},
    };
    const state = api.getState();
    const { datapointId, refresh, siteId, startdate, stopdate } =
      options.options;
    const expectedDateSignature = `${startdate}${stopdate}`;
    const datapointData =
      state.appData.buildingData?.[siteId]?.datapoints?.[datapointId];
    // see if we have this data locally already in the store
    const doFetch =
      refresh ||
      datapointData?.datesignature !== expectedDateSignature ||
      Date.now() - datapointData?.vintage > 3 * MILLIS_PER_MINUTE;

    if (!doFetch) return;

    const response = await talkToServer(request);
    if (response) {
      return {
        response, // pass along what we get back from the server
      };
    }
  },
);

const setWeather = ({ state, siteId, component, key, data }) => {
  if (state.buildingData?.[siteId]) {
    if (!state.buildingData[siteId][component]) {
      state.buildingData[siteId][component] = {};
    }
    state.buildingData[siteId][component][key] = data;
  }
};

export const getBuildingDatapointsData = createAsyncThunk(
  "appdata/getBuildingDatapointsData",
  async (
    {
      component,
      datapointId,
      dateRange,
      siteId,
      station,
      options,
      isShowAnalysis,
      isShowStartups,
    },
    { dispatch },
  ) => {
    await dispatch(
      getIntervalsForDateRange({
        options,
      }),
    );
    const start = `${formatDate(dateRange[0])} 00:00:00`;
    const end = `${formatDate(dateRange[1])} 23:59:59`;

    await dispatch(
      getHourlyWeatherDataByStationAndTimestampRange({
        component,
        siteId,
        start,
        end,
      }),
    );
    await dispatch(
      getForecastWeatherDataByStationAndLocalTimestampRange({
        component,
        siteId,
        station,
        start,
        end,
      }),
    );
    if (isShowAnalysis) {
      await dispatch(
        getOperationsDataByDatapointIdAndTimestampBetween({
          component,
          datapointId,
          siteId,
          start,
          end,
        }),
      );
    }
    if (isShowStartups) {
      await dispatch(
        getStartupsBySiteIdAndTimestampBetween({
          siteId,
          start,
          end,
        }),
      );
    }

    return {
      response: {
        message: "done",
      },
    };
  },
);

export const getEquipmentForSite = getFromServerTopic({
  topic: "equipment",
  action: "getEquipmentForSite",
  contextOptionsFn: ({ arg }) => arg,
  payloadFn: async (arg) => arg,
});

export const appDataSlice = createSlice({
  name: "appdata",
  initialState: {
    // add any default values we might care about here
    buildingData: {},
    chart: {
      multiIAQ: {
        tooltipData: {},
      },
    },
    lastError: null,
    admin: {
      buildingData: {},
    },
    login: {
      status: LOADING,
    },
    mostRecentOrSimilar: {},
    manageInfo: {
      dirty: false,
      isRecalculateHistory: false,
    },
    notes: {
      entitySharingContacts: {
        datapoints: {},
        sites: {},
      },
      notesById: {},
    },
    wellstatDumps: { status: 0, count: 0 },
    ssTableData: {},
    ssTableDataForExport: {},
    intervalDataByDate: { dataview: [] },
    dmDeviceInfo: {},
    status: IDLE,
  },
  reducers: {
    changeComfortZoneLowTempF: (state, action) => {
      state.manageInfo = {
        ...state.manageInfo,
        comfortZoneLowTempF: action.payload,
        dirty: true,
      };
    },
    changeComfortZoneHighTempF: (state, action) => {
      state.manageInfo = {
        ...state.manageInfo,
        comfortZoneHighTempF: action.payload,
        dirty: true,
      };
    },
    changeComfortZoneStart: (state, action) => {
      state.manageInfo = {
        ...state.manageInfo,
        comfortZoneStartTime: action.payload,
        dirty: true,
      };
    },
    changeComfortZoneStop: (state, action) => {
      state.manageInfo = {
        ...state.manageInfo,
        comfortZoneStopTime: action.payload,
        dirty: true,
      };
    },
    changeIsRecalculateHistory: (state) => {
      state.manageInfo = {
        ...state.manageInfo,
        isRecalculateHistory: !state.manageInfo.isRecalculateHistory,
      };
    },
    // used for table sorting, filtering etc
    handleBuildingsTableAttributes: (state, action) => {
      logFilter("appData", "handleBuildingsList", action.payload);
      state.buildingsTableAttributes = {
        ...state.buildingsTableAttributes,
        ...action.payload,
      };
    },
    // save tab state in buildings table
    handleBuildingsTabs: (state, action) => {
      logFilter("appData", "handleBuildingsTabs", action.payload);
      state.buildingsTabs = action.payload;
    },
    // save tab state in EIMS table
    handleEIMSTabs: (state, action) => {
      logFilter("appData", "handleEIMSTabs", action.payload);
      state.EIMSTabs = action.payload;
    },
    // set data for export into Excel doc
    handleSetTableDataForExport: (state, action) => {
      logFilter("appData", "handleSetTableDataForExport", action.payload);
      state.tableDataForExport = action.payload;
    },
    // clear last error
    clearLastError: (state, action) => {
      logFilter("appData", "clearLastError", action.payload);
      state.lastError = null;
    },
    resetWellstatDumpStatus: (state) => {
      logFilter("appData", "resetWellstatDumpStatus");
      state.wellstatDumps.status = 0;
    },
    setChartTooltipData: (state, action) => {
      const { siteId, data } = action.payload;
      state.chart.multiIAQ.tooltipData[siteId] = data;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getBuildingDatapointsData.pending, (state, action) => {
        const { component, siteId } = action.meta.arg;
        state.buildingData[siteId][component] = {
          ...state.buildingData[siteId][component],
          buildingDatapointsStatus: LOADING,
        };
      })
      .addCase(getBuildingDatapointsData.fulfilled, (state, action) => {
        const { component, siteId } = action.meta.arg;
        state.buildingData[siteId][component].buildingDatapointsStatus = IDLE;
      })
      .addCase(getBuildingTableData.fulfilled, (state, action) => {
        const isForExport = action.meta.arg.forExport;
        state.status = IDLE;
        logFilter(
          "appData:getBuildingTableData",
          "getBuildingTableData.fulfilled",
          state,
          action,
        );
        if (action.payload) {
          const { response } = action.payload;
          const { result } = response.payload;

          if (response.rc === "OK") {
            if (!isForExport) {
              state.buildingsTableData = result;
            }
          } else {
            logFilter(
              "appData:getBuildingTableData",
              "setting lastError",
              response,
            );
            state.lastError = response.context;
          }
        } else {
          logFilter(
            "appData:getBuildingTableData",
            "NO building list payload to store",
          );
        }
      })
      .addCase(getDataPointsForSite.fulfilled, (state, action) => {
        logFilter(
          "appData::getDataPointsForSite",
          "getDataPointsForSite.fulfilled",
          state,
          action,
        );
        if (action.payload) {
          // logFilter("appData::getDataPointsForSite", "storing payload", action);
          if (action.payload.response.rc === "OK") {
            state.buildingData[`${action.payload.response.context.site_id}`] = {
              ...(state.buildingData[
                `${action.payload.response.context.site_id}`
              ] || {}),
            };

            state.buildingData[`${action.payload.response.context.site_id}`] = {
              ...state.buildingData[
                `${action.payload.response.context.site_id}`
              ],
              datapoints: {
                ...state.buildingData[
                  `${action.payload.response.context.site_id}`
                ].datapoints,
                ...action.payload.response.payload,
              },
            };
            // state.datapoints[`${action.payload.response.context.site_id}_${action.payload.response.context.type}`] = action.payload.response.payload
          } else {
            logFilter("appData", "setting lastError", action.payload.response);
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter("appData", "NO datapoints list payload to store");
        }
      })
      .addCase(getIntervalsForDateRange.fulfilled, (state, action) => {
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            const fetchKey =
              (action.payload.response.context.options &&
                action.payload.response.context.options.fetchKey) ||
              "unrecognized";
            const newIntervalData = state.intervalDataByDate[fetchKey]
              ? { ...state.intervalDataByDate[fetchKey] }
              : {};
            action.payload.response.payload.map((entry) => {
              const datekey = entry.date; //.replaceAll('-', '_');
              const dpkey = entry.datapoint_id;
              if (!newIntervalData[datekey]) {
                newIntervalData[datekey] = {};
              }
              newIntervalData[datekey][dpkey] = entry;
            });
            state.intervalDataByDate[fetchKey] = newIntervalData;
          } else {
            logFilter("appData", "setting lastError", action.payload.response);
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter("appData", "NO datapoints list payload to store");
        }
      })
      .addCase(getBuildingInfo.fulfilled, (state, action) => {
        const tag = "appData::getBuildingInfo";
        logFilter(tag, "getBuildingInfo.fulfilled", state, action);
        if (action.payload) {
          const response = action.payload.response;

          if (response.rc === "OK") {
            const time = (s) => {
              if (!s) return;

              const timeParts = s.split(":");
              const d = new Date(1970, 0, 1);
              d.setHours(parseInt(timeParts[0]));
              d.setMinutes(parseInt(timeParts[1]));
              return d;
            };

            const siteId = response.context.site_id;
            const data = response.payload;
            state.buildingData[`${siteId}`] = {
              ...state.buildingData[`${siteId}`],
              info: {
                vintage: new Date().getTime() / 1000,
                data,
                weekdayStartTime: time(data.weekday_start),
                weekdayEndTime: time(data.weekday_end),
                saturdayStartTime: time(data.saturday_start),
                saturdayEndTime: time(data.saturday_end),
                sundayStartTime: time(data.sunday_start),
                sundayEndTime: time(data.sunday_end),
              },
            };

            state.manageInfo = {
              comfortZoneLowTempF: data.comfortzone_low_tempf,
              comfortZoneHighTempF: data.comfortzone_high_tempf,
              comfortZoneStartTime: time(data.comfortzone_start_time),
              comfortZoneStopTime: time(data.comfortzone_stop_time),
            };
          } else {
            logFilter("appData", "setting lastError", response);
            state.lastError = response.context;
          }
        } else {
          logFilter("appData", "NO building data list payload to store");
        }
      })
      .addCase(getActiveAccountsForSite.fulfilled, (state, action) => {
        const { meta } = action;
        const siteId = meta.arg.options.site_id;

        const tag = "appData::getActiveAccountsForSite";
        logFilter(tag, "getActiveAccountsForSite.fulfilled", state, action);
        if (action.payload) {
          const { response } = action.payload;
          if (response.rc === "OK") {
            state.buildingData[`${siteId}`] = {
              ...state.buildingData[`${siteId}`],
              accounts: {
                vintage: new Date().getTime() / 1000,
                data: response.payload.result,
              },
            };
          } else {
            logFilter("appData", "setting lastError", response);
            state.lastError = response.context;
          }
        } else {
          logFilter(
            "appData",
            "NO building data account list payload to store",
          );
        }
      })
      .addCase(getAllAccountsForSite.fulfilled, (state, action) => {
        const { meta } = action;
        const siteId = meta.arg.options.site_id;

        const tag = "appData::getAllAccountsForSite";
        logFilter(tag, "getAllAccountsForSite.fulfilled", state, action);
        if (action.payload) {
          const { response } = action.payload;
          if (response.rc === "OK") {
            state.admin.buildingData[`${siteId}`] = {
              ...state.admin.buildingData[`${siteId}`],
              accounts: {
                vintage: new Date().getTime() / 1000,
                data: response.payload.result,
              },
            };
          } else {
            logFilter("appData", "setting lastError", response);
            state.lastError = response.context;
          }
        } else {
          logFilter(
            "appData",
            "NO building data account list payload to store",
          );
        }
      })
      .addCase(getBuildingLayout.fulfilled, (state, action) => {
        const tag = "appData::getBuildingLayout";
        logFilter(tag, "getBuildingLayout.fulfilled", state, action);
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            state.buildingData[`${action.payload.response.context.site_id}`] = {
              ...state.buildingData[
                `${action.payload.response.context.site_id}`
              ],
              layout: {
                vintage: new Date().getTime() / 1000,
                data: action.payload.response.payload,
              },
            };
          } else {
            logFilter("appData", "setting lastError", action.payload.response);
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter("appData", "NO building layout data payload to store");
        }
      })
      .addCase(getEnergyStarRatings.fulfilled, (state, action) => {
        const tag = "appData::getEnergyStarRatings";
        logFilter(tag, "getEnergyStarRatings.fulfilled", state, action);
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            state.buildingData[`${action.payload.response.context.site_id}`] = {
              ...state.buildingData[
                `${action.payload.response.context.site_id}`
              ],
              energyStarRatings: {
                vintage: new Date().getTime() / 1000,
                data: action.payload.response.payload,
              },
            };
          } else {
            logFilter("appData", "setting lastError", action.payload.response);
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter("appData", "NO building data list payload to store");
        }
      })
      .addCase(getDatapointIntervalsForChart.fulfilled, (state, action) => {
        const tag = "appData::getDatapointIntervalsForChart";
        logFilter(
          tag,
          "getDatapointIntervalsForChart.fulfilled",
          state,
          action,
        );
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            const { site_id, datapoint_id, datesignature } =
              action.payload.response.context;

            state.buildingData[`${site_id}`].datapoints = {
              ...state.buildingData[site_id].datapoints,
              [datapoint_id]: {
                vintage: new Date().getTime() / 1000,
                datesignature,
                data: action.payload.response.payload,
              },
            };
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No intervalsforchart payload to store");
        }
      })
      .addCase(deleteNote.pending, (state, action) => {
        const { isPortfolio, siteId } = action.meta.arg;
        if (isPortfolio) {
          state.notes.portfolio = null;
        } else {
          state.notes[siteId] = null;
        }
      })
      .addCase(getEntitySharingContacts.fulfilled, (state, action) => {
        const { datapointId, siteId } = action.meta.arg;
        if (datapointId) {
          state.notes.entitySharingContacts.datapoints[datapointId] =
            action.payload.response;
        } else {
          state.notes.entitySharingContacts.sites[siteId] =
            action.payload.response;
        }
      })
      .addCase(getNotes.fulfilled, (state, action) => {
        const { meta } = action;
        const { isPortfolio, siteId } = meta.arg;
        if (isPortfolio) {
          state.notes.portfolio = action.payload.response;
        } else {
          state.notes[siteId] = action.payload.response;
        }
      })
      .addCase(initializeLoginPage.pending, (state) => {
        state.login.status = LOADING;
      })
      .addCase(initializeLoginPage.fulfilled, (state, action) => {
        const { payload } = action;
        const ssoDomains = payload.response;
        state.login = {
          ...state.login,
          ssoDomains,
          status: IDLE,
        };
      })
      .addCase(getNoteDetails.fulfilled, (state, action) => {
        const { meta } = action;
        const { noteId } = meta.arg;
        state.notes.notesById[noteId] = action.payload.response;
      })
      .addCase(getUtilityAccounts.fulfilled, (state, action) => {
        const tag = "appData::getUtilityAccounts";
        logFilter(tag, "getUtilityAccounts.fulfilled", state, action);
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            state.buildingData[
              `${action.payload.response.context.site_id}`
            ].utilityInfo = action.payload.response.payload;
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No intervalsforchart payload to store");
        }
      })
      .addCase(getWellstatWAQForDeviceManagement.fulfilled, (state, action) => {
        const tag = "appData::getWellstatWAQForDeviceManagement";
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            state.dmDeviceInfo[
              action.payload.response.context.options.serialnumber
            ] = {
              vintage: new Date().getTime() / 1000,
              data: action.payload.response.payload.waq,
            };
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No DeviceManagement payload to store");
        }
      })
      .addCase(getWellstatWBRForDeviceManagement.fulfilled, (state, action) => {
        const tag = "appData::getWellstatWBRForDeviceManagement";
        logFilter(
          tag,
          "getWellstatWBRForDeviceManagement.fulfilled",
          state,
          action,
        );
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            state.dmDeviceInfo[
              action.payload.response.context.options.serialnumber
            ] = {
              vintage: new Date().getTime() / 1000,
              data: action.payload.response.payload.wbr,
            };
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No DeviceManagement payload to store");
        }
      })
      .addCase(getWellstatDevicesForSite.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(getWellstatDevicesForSite.fulfilled, (state, action) => {
        const tag = "appData::getWellstatDevicesForSite";
        logFilter(tag, "getWellstatDevicesForSite.fulfilled", state, action);
        state.status = IDLE;
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            const siteId = action.payload.response.context.options.site_id;

            let forBuilding = state.buildingData[`${siteId}`];
            if (!forBuilding) {
              forBuilding = {};
              state.buildingData[`${siteId}`] = forBuilding;
            }

            forBuilding.wellstatInfo = {
              vintage: new Date().getTime() / 1000,
              data: action.payload.response.payload,
              status: IDLE,
            };
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No wellstatInfo payload to store");
        }
      })
      .addCase(getWellstatDataForDevices.fulfilled, (state, action) => {
        const tag = "appData::getWellstatDataForDevices";
        logFilter(tag, "getWellstatDataForDevices.fulfilled", state, action);
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            action.payload.response.context.options.serialnumbers.map((sn) => {
              state.wellstatDumps[sn] = {
                vintage: new Date().getTime() / 1000,
                data: action.payload.response.payload[sn],
              };
            });
            state.wellstatDumps.status = 1;
            state.wellstatDumps.count++;
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No wellstatInfo payload to store");
        }
      })
      .addCase(
        getOperationsDataByDatapointIdAndTimestampBetween.fulfilled,
        (state, action) => {
          const { meta } = action;
          const { component, siteId } = meta.arg;
          const forBuilding = state.buildingData?.[siteId]?.[component];

          forBuilding.operationsData = action.payload.response;
        },
      )
      .addCase(
        getStartupsBySiteIdAndTimestampBetween.fulfilled,
        (state, action) => {
          const { meta } = action;
          const { siteId } = meta.arg;
          const forBuilding = state.buildingData?.[siteId];

          forBuilding.startups = action.payload.response;
        },
      )
      .addCase(
        getHourlyWeatherDataByStationAndTimestampRange.fulfilled,
        (state, action) => {
          const component = action.meta.arg.component;
          const { data, siteId } = action.payload.response;

          setWeather({
            state,
            siteId,
            component,
            key: "historicalWeather",
            data,
          });
        },
      )
      .addCase(
        getForecastWeatherDataByStationAndLocalTimestampRange.fulfilled,
        (state, action) => {
          const component = action.meta.arg.component;
          const { data, siteId } = action.payload.response;

          setWeather({
            state,
            siteId,
            component,
            key: "forecastWeather",
            data,
          });
        },
      )
      .addCase(getMostRecentOrSimilarDayData.pending, (state, action) => {
        const siteId = action.meta.arg.siteId;
        state.mostRecentOrSimilar[siteId] = {
          ...state.mostRecentOrSimilar[siteId],
          status: LOADING,
        };
      })
      .addCase(getMostRecentOrSimilarDayData.fulfilled, (state, action) => {
        const { meta, payload } = action;
        const { data } = payload.response;
        const { siteId } = meta.arg;

        state.mostRecentOrSimilar[siteId] = {
          ...state.mostRecentOrSimilar[siteId],
          data,
          status: IDLE,
        };
      })
      .addCase(initializeBuilding.pending, (state, action) => {
        const siteId = action.meta.arg.siteId;
        state.buildingData[siteId] = {
          ...state.buildingData[siteId],
          status: LOADING,
        };
      })
      .addCase(initializeBuilding.fulfilled, (state, action) => {
        const siteId = action.meta.arg.siteId;
        state.buildingData[siteId].status = IDLE;
      })
      .addCase(saveNote.pending, (state, action) => {
        const { isPortfolio, siteId } = action.meta.arg;
        if (isPortfolio) {
          state.notes.portfolio = null;
        } else {
          state.notes[siteId] = null;
        }
      })
      .addCase(ssDataTableRefresh.fulfilled, (state, action) => {
        const tag = "appData::ssDataTableRefresh";
        logFilter(tag, "ssDataTableRefresh.fulfilled", state, action);
        if (action.payload) {
          if (action.payload.response.rc === "OK") {
            const key =
              action.payload.response.context.options.fetchKey ||
              action.payload.response.context.options.flavor;
            if (!action.payload.response.context.options.forExport) {
              state.ssTableData[key] = action.payload.response.payload;
            }
          } else {
            logFilter(
              "appData",
              "@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@setting lastError",
              action.payload.response,
            );
            state.lastError = action.payload.response.context;
          }
        } else {
          logFilter(tag, "No ssTableDataRefresh payload to store");
        }
      })
      .addCase(saveFloorInfo.fulfilled, (state, action) => {
        const { siteId } = action.meta.arg.options;
        const { floorList } = action.meta.arg.payload;
        if (action.payload.response.rc === "OK") {
          if (state.buildingData[siteId]) {
            state.buildingData[siteId].info.data.floor_list = floorList;
          }
        }
      })
      .addCase(getEquipmentForSite.fulfilled, (state, action) => {
        const siteId = action.meta.arg;
        const { response } = action.payload;
        if (state.buildingData[siteId]) {
          state.buildingData[siteId].genericEquipment = response;
        }
      });
  },
});

export const {
  changeComfortZoneLowTempF,
  changeComfortZoneHighTempF,
  changeComfortZoneStart,
  changeComfortZoneStop,
  changeIsRecalculateHistory,
  // building data table stuff
  handleBuildingsTableAttributes,
  handleBuildingsTabs,
  handleEIMSTabs,
  handleSetTableDataForExport,
  clearLastError,
  resetWellstatDumpStatus,
  setChartTooltipData,
} = appDataSlice.actions;

export default appDataSlice.reducer;
